"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useState, Suspense } from "react";
import type { SearchQuery } from "@/types/searchquery";

interface QueryStringBuilderProps {
  sendDataToParent: (query: SearchQuery) => void;
}

const defaultQuery: SearchQuery = {
  CountryId: null,
  ResortId: null,
  ResortIds: [],
  ChaletId: null,
  DateFrom: null,
  DateTo: null,
  Adults: null,
  Children: null,
  Infants: null,
  TransferId: null,
  FoodId: null,
  HireId: null,
  LiftPassId: null,
  AirportFromId: null,
  AirportToId: null,
  Rooms: null,
  Currency: null,
  AttributeRefinerIds: [],
  PageSize: 25,
  PageCount: 0,
  SortDirection: null,
  queryStringNull: true,
  TotalCost: null,
  CountrySearchArray: [],
  CustomSearchArray: [],
  SearchType: "SingleResort",
};

const InnerQueryStringBuilder = ({
  sendDataToParent,
}: QueryStringBuilderProps) => {
  const searchParams = useSearchParams();
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState<SearchQuery>(defaultQuery);

  useEffect(() => {
    if (isInitialized) {
      sendDataToParent(searchQuery);
      return;
    }

    const updatedQuery: Partial<SearchQuery> = { ...defaultQuery };

    Object.keys(defaultQuery).forEach((key) => {
      const lower = key.toLowerCase();
      const param =
        searchParams.get(lower) ?? searchParams.get(key as keyof SearchQuery);

      if (!param) return;

      if (!isNaN(+param) && !param.includes(",")) {
        // Only assign if the key expects a number (narrowing)
        if (
          key === "CountryId" ||
          key === "ResortId" ||
          key === "ChaletId" ||
          key === "Adults" ||
          key === "Children" ||
          key === "Infants" ||
          key === "TransferId" ||
          key === "FoodId" ||
          key === "HireId" ||
          key === "LiftPassId" ||
          key === "AirportFromId" ||
          key === "AirportToId" ||
          key === "Rooms" ||
          key === "PageSize" ||
          key === "PageCount" ||
          key === "TotalCost"
        ) {
          updatedQuery[key as keyof SearchQuery] = Number(param) as never;
        }
        return;
      }

      if (param.includes(",")) {
        const values = param
          .split(",")
          .map((v) => (Number.isNaN(Number(v)) ? v : Number(v)));

        // Type narrowing: only assign to keys that are known to be arrays
        if (
          key === "ResortIds" ||
          key === "CountrySearchArray" ||
          key === "CustomSearchArray" ||
          key === "AttributeRefinerIds"
        ) {
          updatedQuery[key as keyof SearchQuery] = values as never;
        }

        return;
      }

      if (
        key === "Currency" ||
        key === "SortDirection" ||
        key === "SearchType"
      ) {
        updatedQuery[key as keyof SearchQuery] = param as never;
      }
    });

    // Special handling for a few keys
    if ((searchParams.get("dateto") || searchParams.get("DateTo")) !== null) {
      updatedQuery.DateTo =
        searchParams.get("dateto") || searchParams.get("DateTo");
    }

    if (
      (searchParams.get("datefrom") || searchParams.get("DateFrom")) !== null
    ) {
      updatedQuery.DateFrom =
        searchParams.get("datefrom") || searchParams.get("DateFrom");
    }

    // Fallbacks for required defaulted arrays
    updatedQuery.ResortIds ??= [];
    updatedQuery.AttributeRefinerIds ??= [];
    updatedQuery.CountrySearchArray ??= [];
    updatedQuery.CustomSearchArray ??= [];

    updatedQuery.queryStringNull = Array.from(searchParams).length === 0;

    const fullQuery = { ...defaultQuery, ...updatedQuery } as SearchQuery;

    setSearchQuery(fullQuery);
    setIsInitialized(true);
    sendDataToParent(fullQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isInitialized, sendDataToParent]);

  return null;
};

const QueryStringBuilder = (props: QueryStringBuilderProps) => {
  return (
    <Suspense fallback={null}>
      <InnerQueryStringBuilder {...props} />
    </Suspense>
  );
};

export default QueryStringBuilder;
